import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { MentionNodeChipContent as MentionNodeChipContentView, MentionNodeTextContent as MentionNodeTextContentView, MentionNodeVisual, } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNodeContent.js';
import { MENTION_NODE_CHIP_CLASS_NAME, MENTION_NODE_CHIP_TEXT_CLASS_NAME, MENTION_NODE_ICON_SIZE_PX, TOOLTIP_MAX_NUM_CHARACTERS, getDefaultMentionIcon, getMentionNodeDisplayText, isMentionNodeVariant, parseSerializedMentionIcon, serializeMentionIcon, serializeMentionNodeProps, } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNodeShared.js';
import { getMentionLinkMarkdown } from '#pplx/frontend/composer-sdk/lexical/utils/getMentionLinkMarkdown.js';
import { truncateText } from '#pplx/frontend/composer-sdk/lexical/utils/truncateText.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import classNames from 'classnames';
import { $applyNodeReplacement, $getNodeByKey, $isTextNode, DecoratorNode } from 'lexical';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
export const MentionNodeChipContent = MentionNodeChipContentView;
export const MentionNodeTextContent = MentionNodeTextContentView;
const MentionNodeDecorator = React.memo(function MentionNodeDecorator({ nodeKey, ...props }) {
    // TODO: Switch on __variant for different mentions
    const [editor] = useLexicalComposerContext();
    const isEditable = useLexicalEditable();
    const [isSelected] = useLexicalNodeSelection(nodeKey);
    const { $t } = useIntl();
    const handleRemove = useCallback(() => {
        editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if (!node)
                return;
            // Mentions are inserted with a trailing space; drop it too so removal doesn't leave a
            // stray space behind.
            const nextSibling = node.getNextSibling();
            node.remove();
            if ($isTextNode(nextSibling) && nextSibling.getTextContent().startsWith(' ')) {
                const remaining = nextSibling.getTextContent().slice(1);
                if (remaining === '') {
                    nextSibling.remove();
                }
                else {
                    nextSibling.setTextContent(remaining);
                }
            }
        });
    }, [editor, nodeKey]);
    const effectiveIcon = props.icon ?? getDefaultMentionIcon(props.mentionVariant);
    // Only an editable input lets the user delete a mention. Read-only displays
    // (e.g. a rendered past query) keep the bare chip with no remove affordance.
    const showRemoveButton = isEditable;
    if (!showRemoveButton) {
        return _jsx(MentionNodeChipContent, { ...props, icon: effectiveIcon, size: "base" });
    }
    const displayText = getMentionNodeDisplayText({
        text: props.text,
        localizedName: props.localizedName,
        mentionVariant: props.mentionVariant,
        prefixWithSlash: props.prefixWithSlash,
    });
    const tooltipContent = $t({
        defaultMessage: 'Remove {mention}',
        description: 'Tooltip on a mention chip indicating that clicking it removes the mention. The placeholder {mention} is replaced with the text of the mention.',
        id: 'e87c651020',
    }, { mention: displayText });
    // Aether Chip has a fixed 32px height. Keep this primitive line-height-sized so mentions do
    // not expand the surrounding editor line box.
    return (_jsx(Tooltip, { content: tooltipContent, skipDelayDurationMs: 0, children: _jsxs(ButtonPrimitive, { interactableVariant: "alt", "aria-label": tooltipContent, className: classNames('group group/mention inline-flex cursor-pointer select-none items-baseline text-primary', MENTION_NODE_CHIP_CLASS_NAME, isSelected
                ? 'bg-super-soft ring-1 ring-inset ring-super-subtle hover:bg-super-soft focus-visible:bg-super-soft'
                : 'hover:bg-subtle focus-visible:bg-subtle'), onMouseDown: (event) => {
                // Lexical selects the isolated decorator node on mousedown; suppress that default
                // so the press stays on this chip instead of moving the editor selection.
                event.preventDefault();
                // Keep the editor root's mousedown handler from re-focusing the input mid-removal.
                event.stopPropagation();
            }, onClick: handleRemove, children: [_jsxs("span", { className: "relative mr-1 inline-flex size-[18px] shrink-0 self-center items-center justify-center", children: [_jsx(MentionNodeVisual, { icon: effectiveIcon, iconClassName: classNames('size-[18px] transition-opacity duration-150 group-hover:opacity-0 group-focus-visible:opacity-0', isSelected ? 'text-super-primary' : 'text-primary'), emojiClassName: "text-[0.80rem] transition-opacity duration-150 group-hover:opacity-0 group-focus-visible:opacity-0" }), _jsx(IconPrimitive, { icon: $icon('x'), size: MENTION_NODE_ICON_SIZE_PX, className: classNames('absolute inset-0 size-[18px] opacity-0 transition duration-150 group-hover:opacity-100 group-focus-visible:opacity-100', isSelected
                                ? 'text-super-primary group-hover:text-super-primary group-focus-visible:text-super-primary'
                                : 'text-secondary group-hover:text-primary group-focus-visible:text-primary') })] }), _jsx(MentionNodeTextContent, { ...props, icon: undefined, tooltipText: undefined, textClassName: MENTION_NODE_CHIP_TEXT_CLASS_NAME, color: isSelected ? 'super' : 'default', size: "base", weight: "semibold", truncate: "large" })] }) }));
});
function $convertMentionElement(domNode) {
    const text = domNode.textContent;
    const uuid = domNode.getAttribute('data-mention-uuid');
    const variant = domNode.getAttribute('data-mention-variant');
    const url = domNode.getAttribute('data-mention-url') ?? undefined;
    const queryText = domNode.getAttribute('data-mention-query-text') ?? undefined;
    const localizedName = domNode.getAttribute('data-mention-localized-name') ?? undefined;
    const icon = parseSerializedMentionIcon(domNode.getAttribute('data-mention-icon'));
    return text !== null && uuid !== null && variant !== null && isMentionNodeVariant(variant)
        ? {
            node: $applyNodeReplacement(new MentionNode({
                uuid,
                variant,
                text,
                url,
                queryText,
                localizedName,
                icon,
            })),
        }
        : null;
}
export class MentionNode extends DecoratorNode {
    __props;
    static getType() {
        return 'mention';
    }
    static clone(node) {
        return new MentionNode(node.__props, node.__key);
    }
    static importJSON(serializedNode) {
        return $applyNodeReplacement(new MentionNode(serializedNode.props)).updateFromJSON(serializedNode);
    }
    constructor(__props, key) {
        super(key);
        this.__props = __props;
    }
    exportJSON() {
        return { ...super.exportJSON(), props: serializeMentionNodeProps(this.__props) };
    }
    createDOM() {
        return document.createElement('span');
    }
    isIsolated() {
        return true;
    }
    updateDOM() {
        return false;
    }
    decorate() {
        return (_jsx(MentionNodeDecorator, { nodeKey: this.getKey(), text: this.__props.text, localizedName: this.__props.localizedName, mentionVariant: this.__props.variant, prefixWithSlash: this.__props.prefixWithSlash, icon: this.__props.icon, tooltipText: truncateText(this.__props.queryText ?? '', TOOLTIP_MAX_NUM_CHARACTERS) }));
    }
    getTextContent() {
        switch (this.__props.variant) {
            case 'shortcut': {
                return this.__props.queryText ?? '';
            }
            case 'skills':
            case 'plugin': {
                const displayLabel = getMentionNodeDisplayText({
                    text: this.__props.text,
                    localizedName: this.__props.localizedName,
                    mentionVariant: this.__props.variant,
                    prefixWithSlash: this.__props.prefixWithSlash,
                });
                return getMentionLinkMarkdown(displayLabel, this.__props.url) ?? displayLabel;
            }
            case 'tab':
            case 'user': {
                return getMentionLinkMarkdown(this.__props.text, this.__props.url) ?? this.__props.text;
            }
            case 'space':
            case 'sources':
            default: {
                return this.__props.text;
            }
        }
    }
    exportDOM() {
        const element = document.createElement('span');
        element.setAttribute('data-mention-uuid', this.__props.uuid);
        element.setAttribute('data-mention-variant', this.__props.variant);
        element.setAttribute('data-mention-url', this.__props.url ?? '');
        element.setAttribute('data-mention-query-text', this.__props.queryText ?? '');
        element.setAttribute('data-mention-localized-name', this.__props.localizedName ?? '');
        const serializedIcon = serializeMentionIcon(this.__props.icon);
        if (serializedIcon) {
            element.setAttribute('data-mention-icon', JSON.stringify(serializedIcon));
        }
        element.textContent = this.__props.text;
        return { element };
    }
    static importDOM() {
        return {
            span: (domNode) => {
                if (!domNode.hasAttribute('data-mention-uuid') ||
                    !domNode.hasAttribute('data-mention-variant')) {
                    return null;
                }
                return {
                    conversion: $convertMentionElement,
                    priority: 4,
                };
            },
        };
    }
}
//# sourceMappingURL=MentionNode.js.map